import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import virtualPosService from '../../../../services/virtualPosService';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Textarea } from '../../../../components/ui/textarea';
import { Switch } from '../../../../components/ui/switch';
import { Alert, AlertDescription } from '../../../../components/ui/alert';
import { ArrowLeft, Save, AlertCircle } from 'lucide-react';

const VirtualPosSubscriptionForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [plans, setPlans] = useState([]);
  const [formData, setFormData] = useState({
    client: {
      email: '',
      first_name: '',
      last_name: '',
      social_id: '',
      phone: ''
    },
    plan_id: '',
    return_url_ok: '',
    return_url_error: '',
    renewal: true
  });

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await virtualPosService.getPlans();
      setPlans(response.plans || []);
    } catch (err) {
      console.error('Error loading plans:', err);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.startsWith('client.')) {
      const clientField = field.replace('client.', '');
      setFormData(prev => ({
        ...prev,
        client: {
          ...prev.client,
          [clientField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await virtualPosService.createSubscription(formData);
      navigate('/payments-admin/virtualpos/subscriptions');
    } catch (err) {
      setError('Error al crear la suscripción');
      console.error('Error creating subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          onClick={() => navigate('/payments-admin/virtualpos/subscriptions')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver
        </Button>
        <h1 className="text-2xl font-bold">Nueva Suscripción VirtualPos</h1>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Datos del Cliente */}
        <Card>
          <CardHeader>
            <CardTitle>Datos del Cliente</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.client.email}
                  onChange={(e) => handleInputChange('client.email', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  value={formData.client.phone}
                  onChange={(e) => handleInputChange('client.phone', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="first_name">Nombre *</Label>
                <Input
                  id="first_name"
                  value={formData.client.first_name}
                  onChange={(e) => handleInputChange('client.first_name', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="last_name">Apellido *</Label>
                <Input
                  id="last_name"
                  value={formData.client.last_name}
                  onChange={(e) => handleInputChange('client.last_name', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="social_id">RUT/ID</Label>
                <Input
                  id="social_id"
                  value={formData.client.social_id}
                  onChange={(e) => handleInputChange('client.social_id', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Configuración de Suscripción */}
        <Card>
          <CardHeader>
            <CardTitle>Configuración de Suscripción</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="plan_id">Plan *</Label>
              <Select value={formData.plan_id} onValueChange={(value) => handleInputChange('plan_id', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar plan" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((plan) => (
                    <SelectItem key={plan.id} value={plan.id.toString()}>
                      {plan.name} - {plan.currency === 'UF' ? `${plan.amount} UF` : new Intl.NumberFormat('es-CL', {
                        style: 'currency',
                        currency: plan.currency
                      }).format(plan.amount)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="return_url_ok">URL de Éxito *</Label>
                <Input
                  id="return_url_ok"
                  type="url"
                  value={formData.return_url_ok}
                  onChange={(e) => handleInputChange('return_url_ok', e.target.value)}
                  placeholder="https://ejemplo.com/success"
                  required
                />
              </div>
              <div>
                <Label htmlFor="return_url_error">URL de Error *</Label>
                <Input
                  id="return_url_error"
                  type="url"
                  value={formData.return_url_error}
                  onChange={(e) => handleInputChange('return_url_error', e.target.value)}
                  placeholder="https://ejemplo.com/error"
                  required
                />
              </div>
            </div>

            {/* Ajuste on/off de la suscripción, no una casilla de una lista:
                switch del DS (ver components/ui/switch.jsx). */}
            <Switch
              id="renewal"
              checked={formData.renewal}
              onCheckedChange={(v) => handleInputChange('renewal', v)}
              label="Renovación automática"
            />
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/payments-admin/virtualpos/subscriptions')}
          >
            Cancelar
          </Button>
          <Button type="submit" disabled={loading}>
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Creando...' : 'Crear Suscripción'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default VirtualPosSubscriptionForm;
